/**
 * RQB Gift Block — Custom web component
 * Identical behavior to gift-wrap-block, but adds _rqb_* properties
 * for the RQB discount function. Reads offer data from embedded JSON.
 */
(() => {
  if (customElements.get('rqb-gift-block')) return;

  function getVisitorId() {
    var id = null;
    try { id = localStorage.getItem('_rqb_visitor_id'); } catch (e) {}
    if (id) return id;
    try {
      var m = document.cookie.split('; ').find(function (c) { return c.indexOf('_rqb_vid=') === 0; });
      if (m) id = m.split('=')[1];
    } catch (e) {}
    if (!id) id = (crypto && crypto.randomUUID ? crypto.randomUUID() : Date.now() + '-' + Math.random().toString(36).slice(2));
    try { localStorage.setItem('_rqb_visitor_id', id); } catch (e) {}
    try { document.cookie = '_rqb_vid=' + id + '; path=/; max-age=63072000; SameSite=Lax'; } catch (e) {}
    return id;
  }

  class RqbGiftBlock extends HTMLElement {
    constructor() {
      super();
      this.closeTimer = null;
      this.lastTrigger = null;
      this.previousBodyOverflow = '';
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      // Parse offer data
      const raw = this.querySelector('script[data-rqb-offer]');
      this.offerData = raw ? JSON.parse(raw.textContent) : null;

      // Cache DOM
      this.modal = this.querySelector('[data-gift-modal]');
      this.sheet = this.querySelector('[data-gift-sheet]');
      this.emptyState = this.querySelector('[data-gift-empty-state]');
      this.selectedState = this.querySelector('[data-gift-selected-state]');
      this.confirmButton = this.querySelector('[data-gift-confirm]');
      this.closeButton = this.querySelector('[data-gift-close]');
      this.backdrop = this.querySelector('[data-gift-backdrop]');
      this.choices = Array.from(this.querySelectorAll('[data-gift-choice]'));
      this.openButtons = Array.from(this.querySelectorAll('[data-gift-open]'));

      this.selectedData = null;

      this.boundKeydown = this.handleKeydown.bind(this);
      this.bindEvents();
      this.syncChoiceState();

      // Intercept the product form submission
      const formId = this.dataset.productFormId;
      if (formId) {
        this.productForm = document.getElementById(formId);
        if (this.productForm) {
          this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
          this.productForm.addEventListener('submit', this.boundHandleFormSubmit, { capture: true });
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.boundKeydown);
      if (this.productForm && this.boundHandleFormSubmit) {
        this.productForm.removeEventListener('submit', this.boundHandleFormSubmit, { capture: true });
      }
      if (this.isOpen()) {
        document.body.style.overflow = this.previousBodyOverflow;
        this.appendChild(this.modal);
        if (this._bodyWrapper?.parentNode) this._bodyWrapper.remove();
      }
    }

    bindEvents() {
      this.openButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.open(btn));
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.open(btn); }
        });
      });
      this.closeButton?.addEventListener('click', () => this.close());
      this.backdrop?.addEventListener('click', () => this.close());
      this.confirmButton?.addEventListener('click', () => this.confirmSelection());
      this.choices.forEach((choice) => {
        choice.addEventListener('change', () => this.syncChoiceState());
      });
    }

    /* ---- Modal open / close ---- */

    open(trigger) {
      if (!this.modal || this.isOpen()) return;
      clearTimeout(this.closeTimer);
      this.lastTrigger = trigger || null;

      if (!this._bodyWrapper) {
        this._bodyWrapper = document.createElement('div');
        this._bodyWrapper.className = 'gift-wrap-block';
      }
      this._bodyWrapper.appendChild(this.modal);
      document.body.appendChild(this._bodyWrapper);

      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.boundKeydown);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._bodyWrapper.classList.add('is-sheet-open');
        });
      });

      setTimeout(() => {
        const checked = this.sheet.querySelector('[data-gift-choice]:checked');
        const first = this.sheet.querySelector('[data-gift-choice]:not(:disabled)');
        (checked || first || this.closeButton)?.focus();
      }, 180);
    }

    close() {
      if (!this.modal || !this.isOpen()) return;
      this._bodyWrapper.classList.remove('is-sheet-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', this.boundKeydown);
      document.body.style.overflow = this.previousBodyOverflow;

      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        this.modal.hidden = true;
        this.appendChild(this.modal);
        if (this._bodyWrapper?.parentNode) this._bodyWrapper.remove();
      }, 310);

      this.lastTrigger?.focus?.();
    }

    isOpen() {
      return this._bodyWrapper?.classList.contains('is-sheet-open') || false;
    }

    /* ---- Selection ---- */

    syncChoiceState() {
      const selected = this.getSelectedChoice();
      this.choices.forEach((choice) => {
        const label = choice.closest('.gift-wrap-block__choice');
        if (label) label.classList.toggle('is-selected', choice === selected);
      });
      if (this.confirmButton) this.confirmButton.disabled = !selected;
    }

    getSelectedChoice() {
      return this.sheet?.querySelector('[data-gift-choice]:checked') || null;
    }

    confirmSelection() {
      const choice = this.getSelectedChoice();
      if (!choice || choice.disabled) return;

      this.selectedData = {
        productId: choice.dataset.productId || '',
        variantId: choice.dataset.variantId || '',
        title: choice.dataset.title || '',
        description: choice.dataset.description || '',
        price: choice.dataset.price || '',
        image: choice.dataset.image || '',
      };

      this.updateSelectedCard(this.selectedData);

      this.dispatchEvent(new CustomEvent('rqb-gift:change', {
        bubbles: true,
        detail: this.selectedData,
      }));

      this.close();
    }

    updateSelectedCard(data) {
      if (!this.emptyState || !this.selectedState) return;

      const img = this.querySelector('[data-selected-image]');
      const title = this.querySelector('[data-selected-title]');
      const desc = this.querySelector('[data-selected-description]');
      const price = this.querySelector('[data-selected-price]');

      if (img) {
        if (data.image) { img.src = data.image; img.alt = data.title; img.hidden = false; }
        else { img.removeAttribute('src'); img.alt = ''; img.hidden = true; }
      }
      if (title) title.textContent = data.title;
      if (desc) { desc.textContent = data.description; desc.hidden = !data.description; }
      if (price) price.textContent = data.price;

      this.emptyState.hidden = true;
      this.selectedState.hidden = false;
    }

    /* ---- Cart: intercept form submit with RQB properties ---- */

    handleFormSubmit(event) {
      if (!this.selectedData?.variantId) return;

      const form = this.productForm;
      if (!form) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const submitButton = form.querySelector('[type="submit"]');
      if (submitButton?.hasAttribute('aria-disabled')) return;

      // Clear errors
      const errMsg = form.querySelector('.product-form__error-message');
      if (errMsg) { errMsg.toggleAttribute('hidden', true); errMsg.innerText = ''; }

      // Read form data
      const formData = new FormData(form);
      const mainVariantId = parseInt(formData.get('id'));
      const quantity = parseInt(formData.get('quantity')) || 1;

      // Collect main product properties
      const mainProperties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value !== '') mainProperties[match[1]] = value;
      }

      // Build RQB metadata
      const offer = this.offerData?.offer;
      const bar = offer?.bars?.find(b => b.enabled !== false) || offer?.bars?.[0];
      const bundleId = 'rqb_' + (offer?.offerId || 'custom') + '_' + Date.now() + Math.random().toString(36).slice(2);
      const visitorId = getVisitorId();

      // RQB properties for the main item
      if (offer?.offerId) {
        mainProperties._rqb_bundle_id = bundleId;
        mainProperties._rqb_role = 'main';
        mainProperties._rqb_bar_position = String(bar?.position ?? 0);
        mainProperties._rqb_bar_id = bar?.id || '';
        mainProperties._rqb_offer_id = offer.offerId;
        mainProperties._rqb_visitor_id = visitorId;
      }

      // Gift item properties
      const giftProps = { '_is_free_gift': 'true' };
      if (offer?.offerId) {
        giftProps._rqb_bundle_id = bundleId;
        giftProps._rqb_role = 'gift';
        giftProps._rqb_offer_id = offer.offerId;
        giftProps._rqb_visitor_id = visitorId;
        giftProps._rqb_line_uid = Date.now() + '_' + Math.random().toString(36).slice(2);
      }

      // Build items
      const items = [
        {
          id: mainVariantId,
          quantity: quantity,
          properties: mainProperties,
        },
        {
          id: parseInt(this.selectedData.variantId),
          quantity: 1,
          parent_id: mainVariantId,
          properties: giftProps,
        },
      ];

      // Gather sections for cart drawer
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      // Show loading
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('btn--loading');

      const lastSubmittedElement = event.submitter || event.currentTarget;
      const isPageCart = document.body.classList.contains('cart-template') || FoxTheme.settings.cartType === 'page';

      const body = JSON.stringify({
        items,
        sections: sectionsToBundle,
        sections_url: window.location.pathname,
      });

      fetch(FoxTheme.routes.cart_add_url, {
        ...FoxTheme.utils.fetchConfig(),
        body,
      })
        .then((r) => r.json())
        .then(async (parsedState) => {
          if (parsedState.status) {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: mainVariantId.toString(),
              errors: parsedState.errors || parsedState.description,
              message: parsedState.message,
            });
            if (errMsg) { errMsg.toggleAttribute('hidden', false); errMsg.innerText = parsedState.description; }
            else if (form.dataset.hideErrors !== 'true') alert(parsedState.description);
            document.dispatchEvent(new CustomEvent('product-ajax:error', { detail: { errorMessage: parsedState.description } }));
            return;
          }

          const cartJson = await (await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig() })).json();
          cartJson.sections = parsedState.sections;

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
          document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState.items?.[0] || parsedState } }));

          if (isPageCart) { window.location.href = FoxTheme.routes.cart_url; return; }

          const cartDrawer = document.querySelector('cart-drawer');
          const upsellWidget = document.querySelector('[js-product-upsell-widget]');
          const isAbTestV1 = document.body.getAttribute('main-product-block-ab-testing-version-1') === 'true';
          const quickViewModal = form.closest('quick-view-modal');

          const openDrawer = () => {
            if (quickViewModal) {
              if (cartDrawer && !cartDrawer.open) {
                document.body.addEventListener(quickViewModal.events.handleAfterHide, () => {
                  setTimeout(() => cartDrawer.show(lastSubmittedElement));
                }, { once: true });
              }
              quickViewModal.hide(true);
            } else {
              cartDrawer && cartDrawer.show(lastSubmittedElement);
            }
          };

          if (!upsellWidget || upsellWidget.classList.contains('hide-product-upsell') || isAbTestV1) {
            openDrawer();
          } else {
            upsellWidget.classList.add('active');
          }

          if (form.closest('[js-product-upsell-widget]')) {
            form.closest('[js-product-upsell-widget]').classList.remove('active');
            openDrawer();
          }
        })
        .catch((error) => {
          console.error('RQB gift add-to-cart error:', error);
        })
        .finally(() => {
          submitButton.classList.remove('btn--loading');
          submitButton.removeAttribute('aria-disabled');
        });
    }

    /* ---- Keyboard / focus ---- */

    handleKeydown(event) {
      if (!this.isOpen()) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (event.key !== 'Tab') return;
      this.trapFocus(event);
    }

    trapFocus(event) {
      if (!this.sheet) return;
      const focusable = Array.from(
        this.sheet.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')
      ).filter(el => !el.hasAttribute('hidden'));

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    }
  }

  customElements.define('rqb-gift-block', RqbGiftBlock);
})();
